import type { PropertyEditFormValues } from "../types"

/**
 * Property module constants — business values, labels, session keys, limits, patterns, and defaults.
 * Keep UI-only Tailwind helpers in component/style files; keep Zod schemas in validators.
 */

export const COMMUNICATION_TYPES: Record<string, string> = {
  PHONE_CALL: 'Phone Call',
  IN_PERSON: 'In Person',
  EMAIL: 'Email',
  INSTANT_MESSAGE: 'Instant Message',
  WEBINAR: 'Webinar',
  TRADE_SHOW: 'Trade Show',
  OTHER: 'Other',
}

export const COMMUNICATION_CATEGORY: Record<string, string> = {
  INITIAL_CONTACT: 'Initial Contact',
  FOLLOW_UP: 'Follow-Up',
  TRAINING: 'Training',
  SYSTEM_HELP: 'System Help',
  PROSPECT: 'Prospect',
  PROPERTY_VISIT: 'Property Visit',
  ZERO_APPS: 'Zero Apps',
  MONTHLY_REPORTING: 'Monthly Reporting',
  OTHER: 'Other',
}

export const COMMUNICATION_DIRECTION: Record<string, string> = { IN: 'In', OUT: 'Out' }

export const INITIAL_PAGINATION = {
  page: 1,
  perPage: 15,
  sortBy: 'desc' as const,
  orderBy: 'log_id' as const,
}

export const PROPERTY_ROLE_NAMES: Record<string, string> = {
  ROLE_PROPERTY_MANAGER: 'Property Manager',
  ROLE_PROPERTY_AGENT: 'Leasing Consultant',
  ROLE_PROPERTY_ADMIN: 'Property Assistant',
  ROLE_REGIONAL_MANAGER: 'Company Regional Manager',
}

export const PAYMENT_TYPES: Record<string, string> = {
  ACH: 'ACH',
  PAPER_CHECK: 'Paper',
}

export const PAYMENT_VERIFY: Record<string, string> = {
  '1': 'Yes',
  '0': 'No',
}

export const REPORT_FREQUENCIES = [
  { label: 'Weekly', value: 'WEEKLY' },
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'None', value: 'NONE' },
]

export const DEFAULT_FORM_VALUES: PropertyEditFormValues = {
  fk_company_id: '',
  property_name: '',
  property_alias_name: '',
  aln_ref_id: '',
  contact_name: '',
  contact_phone: '',
  property_office_phone: '',
  property_address: '',
  property_address2: '',
  property_city: '',
  property_state: '',
  property_zipcode: '',
  is_mailing_address: false,
  mailing_property_address: '',
  mailing_property_address2: '',
  mailing_property_city: '',
  mailing_property_state: '',
  mailing_property_zipcode: '',
  units_count: '',
  listed: '1',
  listed_ids: [],
  occupancy: '',
  rent_amount: '',
  federal_id: '',
  pmc_invite: '1',
  main_contact_person: '',
  policy_id: '',
  website_url: '',
  latitude: '',
  longitude: '',
  email_frequency: 'MONTHLY',
  activate_api: '',
  disable_uc: '0',
  range: 'range_ytd',
  allow_dynamic_access_fee: 0,
}

/** Session key for list filter — matches Angular `authService` / `propertylist_filter`. */
export const PROPERTYLIST_FILTER_SESSION_KEY = 'propertylist_filter'

/**
 * Text filters that should not hit the API on every keystroke.
 * Angular property-list applies these only on "Go" (submit); debouncing approximates that.
 * Same keys as useCompanyList (company list also debounces Liberty ID).
 */
export const DEBOUNCED_FILTER_KEYS = ['liberty_id'] as const

export const DEBOUNCE_MS = 1600

/** Session label fields paired with IDs — not user-facing filter dimensions for badge count. */
export const META_KEYS = new Set([
  'page',
  'perPage',
  'sortBy',
  'orderBy',
  'org_name',
  'company_name',
])

export const MOBILE_PATTERN = /^\d{10,11}$/

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const ADDRESS_WARNING =
  'Warning: Unable to verify address. If you know this address is accurate or is not yet in Google Maps, you may still continue and save this record. Try verifying the address again if the map or property search location is incorrect.'

export const PER_PAGE_OPTIONS = [15, 30, 50, 100]

export const PROPERTY_SEARCH_DEBOUNCE_MS = 500

/** Shown when the API rejects delete without a specific message (matches Angular copy). */
export const CANNOT_DELETE_PRODUCT_MAPPING_MESSAGE =
  'Invite or Application exists with this product for this property. So you cannot delete the product mapping.'

export const DEFAULT_DELETE_CONFIRM_MESSAGE =
  'Are you sure you want to delete this product configuration?'

export const DUPLICATE_MARKER = 'property with this address already'

export const PROPERTY_PHONE_MAX_DIGITS = 12

/** Digit-only validation matching Angular `mobileValidator` after strip: optional 1–2 digit prefix + US NANP 3-3-4. Total 10–12 digits. */
export const PROPERTY_PHONE_DIGITS_REGEX = /^(\d{1,2})?(\d{3})(\d{3})(\d{4})$/

/** Max characters for formatted input (+XX (XXX) XXX-XXXX). */
export const PROPERTY_PHONE_INPUT_MAX_LENGTH = 17

export const PROPERTY_EDIT_ADDRESS_WARNING =
  'Warning: Unable to verify address. If you know this address is accurate or is not yet in Google Maps, you may still continue and save this record. Try verifying the address again if the map location is incorrect.'

/** property-edit.component.ts geocodeAddress — Validation Message dialog body */
export const PROPERTY_ADDRESS_VERIFY_HTML =
  'Warning: Unable to verify address.<br>If you know this address is accurate or is not yet in Google Maps, you may still continue and save this record<br>You may need to enter Latitude and Longitude manually to ensure this property shows correctly on the property search.'

/** property-edit.component.ts geocodeAddress — map banner (`mapMessage`) */
export const PROPERTY_ERROR_ADDRESS_VERIFY =
  'Warning: Unable to verify address. Save it if you have verified it. You may need to enter Latitude and Longitude manually to ensure this property shows correctly on the property search.'

export const PAYMENT_FIELDS = new Set([
  'payment_method',
  'routing_number',
  'account_number',
  'property_method',
  'payee_name',
  'payment_address',
  'payment_shop',
  'payment_state',
  'verified',
  'other_payee_name',
  'payment_city',
  'payment_zipcode',
])

/** Cap visible rows; beyond this the wrap scrolls with a sticky header. */
export const CONTACT_TABLE_SCROLL_AFTER = 5

/** ~header + 5 body rows (py-3 + action icon height). */
export const CONTACT_TABLE_SCROLL_MAX_H = 'max-h-[20rem]'

export * from "./tooltips"
